import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
  getSectionKeyFromPath,
  getPathnameFromFullPath,
  isFormPath,
} from 'new/utils/sectionNavigation';
import { SectionKey } from 'new/state/sectionNavigation/types';
import useSectionNavigationAdapter from 'new/state/sectionNavigation/adapter/useSectionNavigationAdapter';
import useSectionNavigationRepository from 'new/state/sectionNavigation/repository/useSectionNavigation';

/**
 * Hook to track navigation within a section and save the last visited path
 * Includes full path with query parameters
 * Uses Redux for immediate state updates (no localStorage lag)
 */
export function useSectionNavigation(section: SectionKey) {
  const location = useLocation();
  const { saveSectionPath } = useSectionNavigationAdapter();

  useEffect(() => {
    const currentSection = getSectionKeyFromPath(location.pathname);
    if (currentSection === section) {
      const fullPath = location.pathname + location.search + location.hash;
      saveSectionPath(section, fullPath);
    }
  }, [location.pathname, location.search, location.hash, section, saveSectionPath]);
}

/**
 * Hook to get the last visited path for a section, if the user is at the base path.
 * Returns the saved path if:
 * - User is at the base path (with or without trailing slash)
 * - A saved path exists
 * - The saved path is different from default OR has query params/hash
 * - The saved path is still valid (starts with the section's route path)
 *
 * @param section - The section key (e.g., 'integrate', 'discover')
 * @param routePath - The route path for the section (e.g., '/integrate' or '/discover')
 * @param defaultPath - The default path to redirect to if no saved path is valid
 * @returns The last visited path or null
 */
export function useLastVisitedPath(
  section: SectionKey,
  routePath: string,
  defaultPath: string,
): string | null {
  const location = useLocation();
  const { getLastVisitedPath } = useSectionNavigationRepository();
  const savedPath = getLastVisitedPath(section);

  return useMemo(() => {
    const isAtBasePath = location.pathname === routePath || location.pathname === `${routePath}/`;
    if (isAtBasePath) {
      if (savedPath) {
        // Extract pathname from saved path for comparison
        const savedPathname = getPathnameFromFullPath(savedPath);

        // Don't restore form/creation/edit pages - they require proper state/context
        // and can cause issues like infinite loops when users try to exit them
        if (isFormPath(savedPathname, section)) {
          return null;
        }

        // Use saved path if it's different from default or has query params/hash
        if (savedPathname !== defaultPath || savedPath.includes('?') || savedPath.includes('#')) {
          // Verify it's still a valid path for this section
          if (savedPathname.startsWith(routePath)) {
            return savedPath;
          }
        }
      }
    }
    return null;
  }, [location.pathname, routePath, defaultPath, savedPath, section]);
}
